"use client";
/**
 * Post-order next-visit offer (Block 4, overview 7.6 "Post-order thank-you").
 *
 * A single, non-intrusive card on the success screen framed as "Save your next
 * order". The ranker (surface "post_order", capped at 1 server-side) returns one
 * next-visit offer; we show its headline, code (copyable), and a
 * "Save to my offers" action that simply routes to the inbox — the offer is
 * already issued server-side, so saving is a navigation, not a mutation.
 *
 * Renders nothing unless signed in AND the ranker returns an offer.
 */
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Icons } from "@/components/Icons";
import { Button, Card } from "@/components/ui";
import { CodeChip, OfferIcon } from "@/components/offers/OfferBits";
import { useNextBestOffers } from "@/hooks/useOffers";
import { useAuth } from "@/store/auth";
import { useBranch } from "@/store/branch";
import { track } from "@/lib/analytics";

export function PostOrderOffer() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isLoggedIn } = useAuth();
  const { branchId } = useBranch();
  const { offers } = useNextBestOffers("post_order", { branchId });

  const offer = offers[0] ?? null;
  if (!isLoggedIn || !offer) return null;

  const onSave = () => {
    track("offer_saved", { promotion_id: offer.promotionId, surface: "post_order" });
    navigate("/offers");
  };

  return (
    <Card padding={14} style={{ width: "100%", marginTop: 12, textAlign: "start" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <Icons.gift size={15} style={{ color: "var(--primary-700)" }} />
        <span className="display" style={{ fontSize: 14 }}>
          {t("v2.offers.post_order_title")}
        </span>
      </div>
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
        <OfferIcon rewardKind={offer.rewardKind} size={38} glyph={18} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: "var(--ink-1)" }}>
            {offer.headline}
          </div>
          {offer.description && (
            <div className="muted" style={{ fontSize: 12, marginTop: 2, lineHeight: 1.4 }}>
              {offer.description}
            </div>
          )}
          {offer.code && (
            <div style={{ marginTop: 10 }}>
              <CodeChip code={offer.code} t={t} />
            </div>
          )}
        </div>
      </div>
      <Button variant="soft" full onClick={onSave} style={{ marginTop: 14 }}>
        {t("v2.offers.save_to_offers")}
      </Button>
    </Card>
  );
}
